// src/pages/Contact.jsx
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import SocialLinks from '../components/contact/SocialLinks';
import { useAuth } from '../contexts/AuthContext';
import { CONTACT_ENDPOINTS } from '../utils/constants';

import '../styles/pages/contactin.css';

const Contact = () => {
  // State variables
  const [sidebarToggled, setSidebarToggled] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', title: '', footer: '', variant: 'success' });
  
  // Get auth context
  const { token, user } = useAuth();
  
  // When component mounts, pre-fill the email if user is logged in
  useEffect(() => {
    const userDetails = JSON.parse(sessionStorage.getItem('userDetails'));
    if (userDetails && userDetails.Email) {
      setFormData(prev => ({
        ...prev,
        email: userDetails.Email,
        fullName: userDetails.FullName || ''
      }));
    }
  }, []);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Show alert helper
  const showAlert = (title, message, footer = '', variant = 'success') => {
    setAlert({ show: true, title, message, footer, variant });
    
    // Auto dismiss after 5 seconds
    setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }));
    }, 5000);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.message.trim()) {
      showAlert('', 'Please fill in all fields', '', 'danger');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showAlert('', 'Please enter a valid email address', '', 'danger');
      return;
    }
    
    // Send the message
    setLoading(true);
    
    try {
      const response = await fetch(CONTACT_ENDPOINTS.SUBMIT_FORM, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          message: formData.message
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }
      
      // Clear the form
      setFormData({
        fullName: '',
        email: '',
        message: ''
      });
      
      // Show success message
      showAlert(
        '',
        'Your message has been sent!',
        '',
        'success'
      );
    } catch (error) {
      console.error('Error sending message:', error);
      showAlert(
        '',
        error.message || 'Failed to send message',
        '',
        'danger'
      );
    } finally {
      setLoading(false);
    }
  };
  
  // Function to hide inheritance dropdown based on subscription type
  useEffect(() => {
    const hideInheritanceDropdown = () => {
      const userDetails = JSON.parse(sessionStorage.getItem('userDetails')) || {};
      const subscriptionType = userDetails.SubscriptionType;
      
      // Pass this info to Sidebar component
      return subscriptionType === "1" || subscriptionType === "3";
    };
    
    // We'll pass this to the Sidebar component
    hideInheritanceDropdown();
  }, []);
  
  return (
    <div id="page-top">
      <div id="wrapper" className="d-flex">
        <Sidebar 
          toggled={sidebarToggled} 
          setToggled={setSidebarToggled} 
          hideInheritance={
            (JSON.parse(sessionStorage.getItem('userDetails')) || {}).SubscriptionType === "1" || 
            (JSON.parse(sessionStorage.getItem('userDetails')) || {}).SubscriptionType === "3"
          } 
        />
        
        <div id="content-wrapper" className={`d-flex flex-column ${sidebarToggled ? 'content-toggled' : ''}`}>
          <div id="content">
            <DashboardHeader toggleSidebar={() => setSidebarToggled(!sidebarToggled)} />
            
            <Container fluid className="contact-container">
              {/* Alert */}
              {alert.show && (
                <Alert 
                  variant={alert.variant} 
                  onClose={() => setAlert(prev => ({ ...prev, show: false }))} 
                  dismissible
                  className="mb-4"
                >
                  <Alert.Heading>{alert.title}</Alert.Heading>
                  <p>{alert.message}</p>
                  {alert.footer && <p className="mb-0 font-italic">{alert.footer}</p>}
                </Alert>
              )}
              
              {/* Page Heading */}
              <div className="d-flex justify-content-center">
                <h1 className="h1 mb-5 text-gray-800">Contact Us</h1>
              </div>
              
              {/* Contact Form and Info */}
              <Row>
                {/* Contact Form Column */}
                <Col lg={7} className="mb-4">
                  <Card className="shadow contact-form-card">
                    <Card.Body className="custom-card">
                      <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label>Full Name (Alias)</Form.Label>
                          <Form.Control
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                            disabled={loading}
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Email Address</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            disabled={loading}
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Message</Form.Label>
                          <Form.Control
                            as="textarea"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={5}
                            required
                            disabled={loading}
                          />
                        </Form.Group>
                        <Button 
                          variant="success" 
                          type="submit" 
                          className="send-message-btn"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="mr-2"
                              />
                              Sending...
                            </>
                          ) : 'Send Message'}
                        </Button>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
                
                {/* Contact Info Column */}
                <Col lg={5}>
                  <Card className="shadow mb-4">
                    <Card.Body className="custom-card moving-border">
                      <h2>Contact Information:</h2>
                      <a 
                        href="mailto:phenixloc@finnovant.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="email-link"
                      >
                        phenixloc@finnovant.com
                      </a>
                    </Card.Body>
                  </Card>
                  
                  <Card className="shadow mb-4">
                    <Card.Body>
                      <h5 className="card-title">Follow Us</h5>
                      <SocialLinks />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      </div>
      
      {/* Scroll to Top Button */}
      <a className="scroll-to-top rounded" href="#page-top">
        <i className="fas fa-angle-up"></i>
      </a>
    </div>
  );
};

export default Contact;